/**
 * CampaignRunSummaryCard — Patch 34.2.1 · Dashboard integration.
 *
 * Renders the workspace's canonical CampaignRun funnel from
 * ``GET /workspaces/{ws}/campaign-runs/summary``. This is the ONE
 * place Dashboard-level campaign numbers come from — no independent
 * stitching.
 */
import { Link } from "react-router-dom";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Skeleton } from "@/shared/ui/skeleton";
import { useCampaignRunSummary } from "@/entities/campaign-run";

export function CampaignRunSummaryCard({ workspaceId }: { workspaceId: string }) {
  const q = useCampaignRunSummary(workspaceId);

  if (q.isLoading) {
    return (
      <Card data-testid="campaign-run-summary-loading" className="border-border/70 bg-card/70 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Campaign Pulse</CardTitle>
          <CardDescription>Loading canonical campaign counters…</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-3 gap-3 sm:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (q.isError || !q.data) {
    return (
      <Card data-testid="campaign-run-summary-error" className="border-red-300/60 bg-card/70 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-red-700">Campaign Pulse unavailable</CardTitle>
          <CardDescription>
            Couldn&apos;t reach the canonical projection. Refresh, or check the
            backend health surface.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const s = q.data;
  const c = s.counters;
  const unhealthy = Object.entries(s.runs_by_health)
    .filter(([k]) => k !== "healthy" && k !== "generating")
    .reduce((acc, [, n]) => acc + (n ?? 0), 0);
  const runningCampaigns = (s.runs_by_health.generating ?? 0);

  const tiles: { label: string; value: number; testId: string; tone?: "warn" }[] = [
    { label: "Running", value: runningCampaigns, testId: "tile-running" },
    { label: "Drafts", value: c.draft, testId: "tile-draft" },
    { label: "Approved", value: c.approved, testId: "tile-approved" },
    { label: "Scheduled", value: c.scheduled, testId: "tile-scheduled" },
    { label: "Published", value: c.published, testId: "tile-published" },
    { label: "Failed", value: c.failed, testId: "tile-failed", tone: c.failed ? "warn" : undefined },
  ];

  return (
    <Card data-testid="campaign-run-summary" className="border-border/70 bg-card/70 backdrop-blur">
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="text-lg font-semibold">Campaign Pulse</CardTitle>
          <CardDescription>
            One canonical projection for every campaign in this workspace.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          {unhealthy > 0 && (
            <Badge
              data-testid="badge-unhealthy"
              variant="outline"
              className="border-amber-400 text-amber-700"
            >
              {unhealthy} needs attention
            </Badge>
          )}
          {s.reconciliation_pending > 0 && (
            <Badge
              data-testid="badge-reconciliation-pending"
              variant="outline"
              className="border-amber-400 text-amber-700"
            >
              {s.reconciliation_pending} reconciling
            </Badge>
          )}
          {s.projection_integrity && s.projection_integrity.runs_skipped > 0 && (
            <Badge
              data-testid="badge-projection-skipped"
              variant="outline"
              className="border-red-500 text-red-700"
              title={`Incident ${s.projection_integrity.incident_id ?? ""}`}
            >
              {s.projection_integrity.runs_skipped} pack{s.projection_integrity.runs_skipped === 1 ? "" : "s"} skipped
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
          {tiles.map((t) => (
            <div
              key={t.testId}
              data-testid={`campaign-pulse-${t.testId}`}
              className={
                "rounded-md border border-border/60 bg-background/50 px-3 py-3 " +
                (t.tone === "warn" ? "border-amber-400/70 " : "")
              }
            >
              <div className="text-2xl font-semibold tabular-nums">{t.value}</div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                {t.label}
              </div>
            </div>
          ))}
        </div>
        {c.draft > 0 && (
          <div
            data-testid="pulse-drafts-cta"
            className="flex items-center justify-between rounded-md border border-amber-300/70 bg-amber-50/50 px-3 py-2 text-sm"
          >
            <span>
              <strong>{c.draft}</strong> draft{c.draft === 1 ? "" : "s"} awaiting review.
            </span>
            <Link
              to="/campaigns/review"
              className="font-medium text-amber-800 underline underline-offset-2"
            >
              Open Review Queue
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
